import axios from 'axios';
import MeetingAccountApi from '@hkgai/pb-fe-api/lib/meeting_account_api/meeting_account_api';
import type { GetUserInfoResp as MeetingGetUserInfoResp, UserLoginOutResp as MeetingUserLoginOutResp } from '@hkgai/pb-fe-api/lib/interface/meeting_api';
import {
  GetCreditBalanceReq,
  GetCreditBalanceResp,
  GetCreditSummaryReq,
  GetCreditSummaryResp,
  GetCurrentMembershipReq,
  GetUserInfoReq,
  ListCreditTransactionsReq,
  ListCreditTransactionsResp,
  MembershipCurrent,
  UserLoginOutReq,
} from '@hkgai/pb-fe-api/lib/interface/meeting_account_api';
import { interceptorRequestConfig, interceptorRequestError, interceptorResponseConfig, interceptorResponseError } from './request';
import { REQUEST_TIMEOUT, SERVICE_HOST } from '@/config/constant';

const CREDIT_TRANSACTIONS_EXPORT_PATH = '/meeting/api/v1/account/credits/transactions/export';
const CREDIT_TRANSACTIONS_EXPORT_FALLBACK_NAME = 'credit-transactions-export';

const MIME_TYPE_EXTENSIONS: Record<string, string> = {
  'application/json': '.json',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/zip': '.zip',
  'text/csv': '.csv',
  'text/plain': '.txt',
};

export interface CreditTransactionsExportResult {
  blob: Blob;
  filename: string;
}

interface CreditTransactionsExportTransport {
  post: (url: string, data: Record<string, never>, config: { responseType: 'blob' }) => Promise<{ data: Blob; headers?: unknown }>;
}

const getHeader = (headers: unknown, name: string) => {
  if (!headers || typeof headers !== 'object') {
    return '';
  }

  const headerGetter = (headers as { get?: (headerName: string) => unknown }).get;
  if (typeof headerGetter === 'function') {
    const value = headerGetter.call(headers, name);
    return typeof value === 'string' ? value : '';
  }

  const matchingHeader = Object.entries(headers as Record<string, unknown>).find(([headerName]) => headerName.toLowerCase() === name.toLowerCase());
  return typeof matchingHeader?.[1] === 'string' ? matchingHeader[1] : '';
};

const decodeFilename = (value: string) => {
  const normalizedValue = value
    .trim()
    .replace(/^['"]|['"]$/g, '')
    .replace(/^UTF-8''/i, '');
  try {
    return decodeURIComponent(normalizedValue);
  } catch {
    return normalizedValue;
  }
};

const sanitizeFilename = (value: string) => {
  const basename = value.split(/[/\\]/).pop() || '';
  const basenameWithoutControlCharacters = [...basename].map(character => (character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127 ? '_' : character)).join('');
  return basenameWithoutControlCharacters
    .replace(/[<>:"|?*]/g, '_')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/g, '')
    .trim();
};

export const resolveCreditTransactionsExportFilename = (contentDisposition?: string, contentType?: string) => {
  const encodedFilenameMatch = contentDisposition?.match(/filename\*\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  const regularFilenameMatch = contentDisposition?.match(/filename\s*=\s*(?:"([^"]*)"|([^;]+))/i);
  const serverFilename = encodedFilenameMatch?.[1] || encodedFilenameMatch?.[2] || regularFilenameMatch?.[1] || regularFilenameMatch?.[2] || '';
  const sanitizedFilename = sanitizeFilename(decodeFilename(serverFilename));
  if (sanitizedFilename) {
    return sanitizedFilename;
  }

  const normalizedContentType = contentType?.split(';')[0].trim().toLowerCase() || '';
  return `${CREDIT_TRANSACTIONS_EXPORT_FALLBACK_NAME}${MIME_TYPE_EXTENSIONS[normalizedContentType] || ''}`;
};

export const createCreditTransactionsExporter = (transport: CreditTransactionsExportTransport) => async (): Promise<CreditTransactionsExportResult> => {
  const response = await transport.post(CREDIT_TRANSACTIONS_EXPORT_PATH, {}, { responseType: 'blob' });
  const contentType = getHeader(response.headers, 'content-type') || response.data.type;
  return {
    blob: response.data,
    filename: resolveCreditTransactionsExportFilename(getHeader(response.headers, 'content-disposition'), contentType),
  };
};

export const meetingAccountApi = new MeetingAccountApi(SERVICE_HOST, {
  timeout: REQUEST_TIMEOUT,
});

meetingAccountApi.serviceInstance.interceptors.request.use(interceptorRequestConfig, interceptorRequestError);
meetingAccountApi.serviceInstance.interceptors.response.use(interceptorResponseConfig, interceptorResponseError);

const creditTransactionsExportApi = axios.create({
  baseURL: SERVICE_HOST,
  timeout: REQUEST_TIMEOUT,
});

creditTransactionsExportApi.interceptors.request.use(interceptorRequestConfig, interceptorRequestError);

export const fetchCreditTransactionsExport = createCreditTransactionsExporter({
  post: (url, data, config) => creditTransactionsExportApi.post<Blob>(url, data, config),
});

export const fetchUserInfo = (params: GetUserInfoReq = {}) => meetingAccountApi.GetUserInfo(params) as unknown as Promise<MeetingGetUserInfoResp>;

export const fetchUserLogOut = (params: UserLoginOutReq = {}) => meetingAccountApi.UserLoginOut(params) as unknown as Promise<MeetingUserLoginOutResp>;

export const fetchCurrentMembership = (params: GetCurrentMembershipReq = {}) => meetingAccountApi.GetCurrentMembership(params) as Promise<MembershipCurrent>;

export const fetchCreditSummary = (params: GetCreditSummaryReq = {}) => meetingAccountApi.GetCreditSummary(params) as Promise<GetCreditSummaryResp>;

export const fetchCreditBalance = (params: GetCreditBalanceReq = {}) => meetingAccountApi.GetCreditBalance(params) as Promise<GetCreditBalanceResp>;

export const fetchCreditTransactions = (params: ListCreditTransactionsReq = {}) => meetingAccountApi.ListCreditTransactions(params) as Promise<ListCreditTransactionsResp>;
